import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Star } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function OwnerRateCustomerDialog({ open, onOpenChange, booking, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (rating < 1) {
      toast.error("Please select a rating");
      return;
    }
    if (comment.trim().length < 3) {
      toast.error("Please write at least a few words");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews/customer", {
        booking_id: booking.id,
        rating,
        comment,
      });
      toast.success("Thanks for rating your customer!");
      onSubmitted?.();
      onOpenChange(false);
      setRating(0);
      setComment("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="owner-rate-customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Rate this customer</DialogTitle>
          <div className="text-sm text-muted-foreground">{booking.customer_name} · {booking.start_date} → {booking.end_date}</div>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">How did they treat your car?</div>
            <div className="flex gap-1.5" data-testid="customer-rating-stars">
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = n <= (hover || rating);
                return (
                  <button
                    key={n}
                    type="button"
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    data-testid={`customer-rating-star-${n}`}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      size={28}
                      className={filled ? "fill-accent text-accent" : "text-muted-foreground/40"}
                    />
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Notes for our records (private, not shown publicly)</div>
            <Textarea
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Returned the car clean and on time…"
              data-testid="customer-review-comment-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="customer-rating-submit-btn">
            {submitting ? "Submitting…" : "Submit rating"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
